import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import imageDocumentsDashboard from "../../../assets/images/icons/documents-dashboard.svg";
import { fleetService } from "../../../api/fleetService/fleetServicesRegistered";
import { BusinessProfile } from "../../../types/profile";
import { useApiCache } from "../../../utils/hooks/useApiCache";

interface EamWidgetProps {
  businessProfile: BusinessProfile;
}

const EamWidget: React.FC<EamWidgetProps> = ({ businessProfile }) => {
  const { t } = useTranslation();
  const [incompleteVehiclesCount, setIncompleteVehiclesCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  const { getCacheKey, isCached, getCachedData, setCachedData, isFetching, setFetching } = useApiCache();

  useEffect(() => {
    if (!businessProfile?.legacycodes || businessProfile.legacycodes.length === 0) {
      setIsLoading(false);
      return;
    }

    const fetchIncompleteVehiclesCount = async () => {
      const firstLegacyCode = businessProfile.legacycodes[0];
      const params = {
        LegacyCode: firstLegacyCode.legacycode,
        Company: firstLegacyCode.company
      };
      
      const cacheKey = getCacheKey('incompleteVehicles', businessProfile, params);
      
      // Controlla se i dati sono in cache
      if (isCached(cacheKey)) {
        console.log('EamWidget: Using cached incomplete vehicles count');
        setIncompleteVehiclesCount(getCachedData(cacheKey));
        setIsLoading(false);
        return;
      }

      // Evita chiamate concorrenti
      if (isFetching(cacheKey)) {
        console.log('EamWidget: Skipping fetch - already fetching');
        return;
      }

      try {
        setFetching(cacheKey, true);
        setIsLoading(true);
        console.log('EamWidget: Fetching incomplete vehicles count');
        
        const incompleteVehiclesRes = await fleetService.getCountIncompleteVehicles(params);
        const count = incompleteVehiclesRes.countIncompleteVehicles;
        
        // Salva in cache
        setCachedData(cacheKey, count, businessProfile);
        setIncompleteVehiclesCount(count);
        console.log('EamWidget: Cached incomplete vehicles count:', count);
      } catch (error) {
        console.error('Error fetching incomplete vehicles count:', error);
        setIncompleteVehiclesCount(0);
      } finally {
        setIsLoading(false);
        setFetching(cacheKey, false);
      }
    };

    fetchIncompleteVehiclesCount();
  }, [businessProfile, getCacheKey, isCached, getCachedData, setCachedData, isFetching, setFetching]);

  const renderSkeletonLoader = () => {
    return (
      <div className="py-4 border-b w-full">
        <div className="font-black text-red text-[44px] leading-[53px] animate-pulse bg-gray-200 h-[53px] w-16 rounded"></div>
      </div>
    );
  };

  if (isNaN(incompleteVehiclesCount)) {
    console.error("Numero non valido:", incompleteVehiclesCount);
    return <div>Errore nel numero di veicoli incompleti</div>;
  }

  return (
    <div className="mb-[56px] md:mb-0 md:bg-lightGrey md:p-6 md:rounded card-shadow">
      <h2 className="font-light text-[24px] leading-[38px]">
        Veicoli incompleti
      </h2>
      
      {isLoading ? (
        renderSkeletonLoader()
      ) : (
        <div className="py-4 border-b w-full">
          <div className="font-black text-red text-[44px] leading-[53px]">
            {incompleteVehiclesCount}
          </div>
        </div>
      )}

      <div className="mt-6">
        <Link className="inline-flex items-center" to="/fleet-and-route">
          <img src={imageDocumentsDashboard} alt="services_icon_bag" />
          <div className="uppercase text-sm leading-[17px] underline text-red font-semibold ml-[6px] mb-[2px]">
          {t("screens.dashboard.widgets.fleet.link")}
          </div>
        </Link>
      </div>
    </div>
  );
};

export default EamWidget;
